import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, MessageCircle, X, Send, ImagePlus, Video, LifeBuoy } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { t, useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

// Floating chat center: FUNZI support (24/7 AI + human escalation), order↔rider
// chats, supply order chats — with auto-translation, images and video rooms.
export const ChatCenter = () => {
  const { user } = useAuth();
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // conversation id
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/conversations`, headers());
      setConvs(data.conversations);
      setUnread(data.conversations.reduce((a, c) => a + (c.unread || 0), 0));
    } catch {
      /* silent */
    }
  }, []);

  const loadThread = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API}/chat/conversations/${id}/messages`, headers());
      setThread(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    loadConvs();
    const t = setInterval(loadConvs, 15000);
    return () => clearInterval(t);
  }, [loadConvs]);

  useEffect(() => {
    if (!open || !active) return;
    loadThread(active);
    const t = setInterval(() => loadThread(active), 8000);
    return () => clearInterval(t);
  }, [open, active, loadThread]);

  // Other surfaces (order timeline, supply orders) can open a chat straight into a thread
  useEffect(() => {
    const handler = async (e) => {
      try {
        const { data } = await axios.post(`${API}/chat/conversations`, e.detail, headers());
        setOpen(true);
        setActive(data.id);
        loadConvs();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    };
    window.addEventListener("funz:open-chat", handler);
    return () => window.removeEventListener("funz:open-chat", handler);
  }, [loadConvs]);

  const openSupport = async () => {
    try {
      const { data } = await axios.post(`${API}/chat/conversations`, { kind: "support" }, headers());
      setActive(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const send = async (imageUrl = "") => {
    if (!text.trim() && !imageUrl) return;
    setBusy(true);
    try {
      await axios.post(`${API}/chat/conversations/${active}/messages`, { text: text.trim(), image_url: imageUrl }, headers());
      setText("");
      loadThread(active);
      loadConvs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const attach = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = String(reader.result).split(",")[1];
        const { data } = await axios.post(`${API}/chat/files`, { data_base64: base64, content_type: file.type, folder: "chat" }, headers());
        send(data.url);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    };
    reader.readAsDataURL(file);
  };

  const startVideo = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/conversations/${active}/video`, headers());
      window.open(data.url, "_blank", "noopener");
      await axios.post(`${API}/chat/conversations/${active}/messages`, { text: `🎥 Video call started — join: ${data.url}` }, headers());
      loadThread(active);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const convOf = (id) => convs.find((c) => c.id === id);

  return (
    <>
      <button
        data-testid="chat-bubble"
        onClick={() => setOpen(!open)}
        aria-label="Chat"
        className="fixed bottom-20 left-4 z-[70] flex h-13 w-13 items-center justify-center rounded-full bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] text-white shadow-xl shadow-[#7C3AED]/30 transition-transform hover:scale-110 lg:bottom-6"
        style={{ height: 52, width: 52 }}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        {!open && unread > 0 && (
          <span data-testid="chat-unread" className="absolute -right-1 -top-1 flex min-w-[18px] items-center justify-center rounded-full bg-[#F97316] px-1 font-jbmono text-[9px] font-bold" style={{ height: 18 }}>
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-[136px] left-4 z-[70] flex h-[480px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0d0f14]/97 shadow-2xl backdrop-blur-2xl lg:bottom-24">
          {!active ? (
            <>
              <div className="border-b border-white/10 p-4">
                <p className="font-display text-sm font-bold">{t(lang, "ch_title")}</p>
                <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">FUNZI · 24/7 · auto-translated</p>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto p-3">
                <button data-testid="chat-new-support" onClick={openSupport}
                  className="flex w-full items-center gap-3 rounded-2xl border border-[#7C3AED]/30 bg-[#7C3AED]/[0.07] p-3.5 text-left transition-colors hover:border-[#7C3AED]/60">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#06B6D4]"><LifeBuoy className="h-4 w-4 text-white" /></span>
                  <span>
                    <span className="block text-xs font-bold">{t(lang, "ch_support")}</span>
                    <span className="block font-jbmono text-[9px] text-slate-500">AI answers instantly · humans when needed</span>
                  </span>
                </button>
                {convs.filter((c) => c.kind !== "support" || c.last_text).map((c) => (
                  <button key={c.id} data-testid={`chat-conv-${c.id.slice(-6)}`} onClick={() => setActive(c.id)}
                    className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3.5 text-left transition-colors hover:border-white/25">
                    <span className="text-lg">{c.kind === "support" ? "🛟" : c.kind === "order" ? "🛵" : "🥕"}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 text-xs font-bold">
                        {c.title}
                        {c.status === "escalated" && <span className="rounded-full bg-[#F97316]/15 px-1.5 py-0.5 font-jbmono text-[8px] font-bold text-[#F97316]">HUMAN</span>}
                      </span>
                      <span className="block truncate font-jbmono text-[10px] text-slate-500">{c.last_text || "…"}</span>
                    </span>
                    {c.unread > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#F97316] px-1 font-jbmono text-[9px] font-bold">{c.unread}</span>}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 border-b border-white/10 p-3">
                <button data-testid="chat-back" onClick={() => setActive(null)} className="rounded-full p-1.5 text-slate-400 hover:text-white">
                  <X className="h-4 w-4" />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold">{thread?.conversation?.title || convOf(active)?.title}</p>
                  <p className="font-jbmono text-[9px] text-slate-500">
                    {thread?.conversation?.status === "escalated" ? `🚨 ${t(lang, "ch_escalated")}` : t(lang, "ch_live")}
                  </p>
                </div>
                <button data-testid="chat-video" onClick={startVideo} title={t(lang, "ch_video")}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-[#06B6D4]/40 text-[#06B6D4] transition-colors hover:bg-[#06B6D4]/10">
                  <Video className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 space-y-2.5 overflow-y-auto p-3" data-testid="chat-thread">
                {!thread ? (
                  <LoaderCircle className="mx-auto mt-8 h-5 w-5 animate-spin text-[#06B6D4]" />
                ) : thread.messages.length === 0 ? (
                  <p className="mt-8 text-center text-xs text-slate-500">Say hi 👋 — FUNZI answers instantly, in your language.</p>
                ) : (
                  thread.messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                          m.kind === "funzi" ? "border border-[#06B6D4]/30 bg-[#06B6D4]/[0.08]" : mine ? "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4]" : "border border-white/10 bg-white/[0.05]"}`}>
                          {!mine && <p className="font-jbmono text-[8px] uppercase tracking-widest text-slate-500">{m.kind === "funzi" ? "🤖 FUNZI" : m.sender_name}</p>}
                          {m.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${m.image_url}`} alt="attachment" className="mb-1.5 max-h-40 rounded-xl border border-white/10" />}
                          {m.text && <p className="text-xs leading-relaxed">{m.text}</p>}
                          {m.translated && <p className="mt-1 font-jbmono text-[8px] text-slate-500" title={m.original}>🌐 auto-translated · original: {m.original.slice(0, 60)}{m.original.length > 60 ? "…" : ""}</p>}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>
              <div className="flex items-center gap-2 border-t border-white/10 p-2.5">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => attach(e.target.files?.[0])} />
                <button data-testid="chat-attach" onClick={() => fileRef.current?.click()} className="rounded-full p-2 text-slate-400 transition-colors hover:text-white">
                  <ImagePlus className="h-4 w-4" />
                </button>
                <input
                  data-testid="chat-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t(lang, "ch_placeholder")}
                  className="h-10 flex-1 rounded-full border border-white/10 bg-white/[0.04] px-4 text-xs text-white placeholder:text-slate-500 outline-none focus:border-[#7C3AED]/60"
                />
                <button data-testid="chat-send" onClick={() => send()} disabled={busy || !text.trim()}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white disabled:opacity-50">
                  {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 rtl:-scale-x-100" />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
